import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, MessageSquareText } from "lucide-react";
import { Button } from "../ui/button";

const ENDPOINT = "https://academic-giraffe-675.convex.site/staff/appointments";
type Note = { id: string; authorName: string; text: string; createdAt: number };
type NotesPage = { page: Note[]; continueCursor: string; isDone: boolean };

export function RequestNotes({ requestId, token, onAccessLost }: { requestId: string; token: string; onAccessLost: () => void }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState<Note[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [done, setDone] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState<"load" | "save" | null>(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const active = useRef<AbortController | null>(null);
  const retry = useRef<{ text: string; key: string } | null>(null);

  useEffect(() => () => { active.current?.abort(); active.current = null; }, [requestId, token]);

  async function run(kind: "load" | "save", older = false) {
    if (active.current || !token || document.hidden) return;
    const value = text.trim();
    if (kind === "save" && (!value || value.length > 2000)) {
      setError("Enter a note of up to 2,000 characters.");
      return;
    }
    if (kind === "save" && retry.current?.text !== value) retry.current = { text: value, key: crypto.randomUUID() };
    const controller = new AbortController();
    active.current = controller;
    const timeout = window.setTimeout(() => controller.abort(), 15_000);
    setBusy(kind); setMessage(""); setError("");
    try {
      const response = await fetch(ENDPOINT, {
        method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(kind === "load"
          ? { operation: "notes", id: requestId, cursor: older ? cursor : null }
          : { operation: "addNote", id: requestId, text: value, requestKey: retry.current!.key }),
        cache: "no-store", credentials: "omit", referrerPolicy: "no-referrer", signal: controller.signal,
      });
      if (active.current !== controller || document.hidden) return;
      if (response.status === 401 || response.status === 403) { onAccessLost(); return; }
      if (!response.ok) throw new Error("unavailable");
      const result = await response.json();
      if (active.current !== controller || document.hidden) return;
      if (kind === "load") {
        const page = result as NotesPage;
        setNotes(previous => older ? [...previous, ...page.page.filter(n => !previous.some(p => p.id === n.id))] : page.page);
        setCursor(page.continueCursor); setDone(page.isDone); setLoaded(true);
      } else {
        const note = result as Note;
        setNotes(previous => [note, ...previous.filter(n => n.id !== note.id)]);
        setText(""); retry.current = null; setMessage("Note saved.");
      }
    } catch {
      if (active.current !== controller || document.hidden) return;
      setError(kind === "save" ? "We couldn’t confirm the save. Your note is still here; try saving again."
        : "Notes couldn’t be loaded. Please try again.");
    } finally {
      window.clearTimeout(timeout);
      if (active.current === controller) { active.current = null; setBusy(null); }
    }
  }

  return (
    <section aria-label="Team notes" className="mt-5 border-t border-[var(--border)] pt-4">
      <button type="button" aria-expanded={open} aria-controls={`notes-${requestId}`} disabled={busy !== null}
        className="flex min-h-11 w-full items-center justify-between gap-3 text-left"
        onClick={() => { setOpen(!open); if (!open) void run("load"); }}>
        <span className="flex items-center gap-2 font-semibold"><MessageSquareText size={18} />Team notes</span>
        <span className="flex items-center gap-1 text-sm text-[var(--accent)]">{open ? "Hide notes" : "Add or view notes"}{open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</span>
      </button>
      {open && <div id={`notes-${requestId}`} className="mt-2 space-y-4">
        <p className="text-sm text-[var(--text-secondary)]">Record how the patient was contacted and what needs to happen next. Notes are shared with the follow-up team.</p>
        {loaded && <div>
          <label htmlFor={`note-${requestId}`} className="mb-2 block text-sm font-medium">Add a note</label>
          <textarea id={`note-${requestId}`} value={text} rows={3} maxLength={2000} disabled={busy !== null}
            style={{ fontSize: 16 }}
            aria-describedby={`note-limit-${requestId}`} placeholder="For example: Called and left a voicemail. Try again tomorrow morning."
            className="block w-full resize-y rounded-lg border border-[var(--border)] bg-[var(--canvas)] px-3 py-2 text-base leading-relaxed text-[var(--text)] focus:outline-2 focus:outline-[var(--accent)]"
            onChange={event => { setText(event.target.value); setMessage(""); setError(""); }} />
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <span id={`note-limit-${requestId}`} className="text-xs text-[var(--text-secondary)]">{text.length.toLocaleString()} / 2,000 characters</span>
            <Button variant="primary" style={{ minHeight: 44 }} disabled={busy !== null || !text.trim()} onClick={() => { void run("save"); }}>{busy === "save" ? "Saving…" : "Save note"}</Button>
          </div>
        </div>}
        {message && <p role="status" className="text-sm text-[var(--accent)]">{message}</p>}
        {error && <div role="alert" className="rounded-lg border border-[var(--border)] p-3 text-sm">{error}
          {!loaded && <Button className="mt-2" disabled={busy !== null} onClick={() => { void run("load"); }}>Try again</Button>}
        </div>}
        {loaded && <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-sm font-semibold">Contact history · newest first</h4>
          <Button variant="ghost" style={{ minHeight: 44 }} disabled={busy !== null} onClick={() => { void run("load"); }}>Refresh notes</Button>
        </div>}
        {busy === "load" && <p role="status" className="text-sm text-[var(--text-secondary)]">Loading notes…</p>}
        {loaded && notes.length === 0 && <p className="text-sm text-[var(--text-secondary)]">No notes yet. Add the first contact update above.</p>}
        <ol className="space-y-3">{notes.map(note => <li key={note.id} className="rounded-lg bg-[var(--canvas)] p-3">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-sm">
            <span className="break-words font-medium">{note.authorName}</span>
            <time dateTime={new Date(note.createdAt).toISOString()} className="text-xs text-[var(--text-secondary)]">{new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" }).format(note.createdAt)}</time>
          </div>
          <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed [overflow-wrap:anywhere]">{note.text}</p>
        </li>)}</ol>
        {loaded && !done && <Button style={{ minHeight: 44 }} disabled={busy !== null} onClick={() => { void run("load", true); }}>Older notes</Button>}
      </div>}
    </section>
  );
}
